/**
 * slotOffers[] from GET /fetchMeta — date‑bounded theatre+slot promotions.
 */

export function normalizeYmd(value) {
  if (value == null) return "";
  const s = String(value).trim();
  if (s.length >= 10) return s.slice(0, 10);
  return s;
}

export function findApplicableSlotOffer(slotOffers, theatreId, slotId, eventYmd) {
  const day = normalizeYmd(eventYmd);
  if (!day || theatreId == null || slotId == null) return null;
  const list = Array.isArray(slotOffers) ? slotOffers : [];
  const tid = String(theatreId);
  const sid = String(slotId);
  for (const o of list) {
    if (!o || typeof o !== "object") continue;
    if (String(o.isActive || "Y").toUpperCase() !== "Y") continue;
    if (String(o.theatreId) !== tid || String(o.slotId) !== sid) continue;
    const from = normalizeYmd(o.validFrom);
    const to = normalizeYmd(o.validTo);
    if (!from || !to) continue;
    if (day >= from && day <= to) return o;
  }
  return null;
}

/** Red chip / label for Book now slot buttons (₹ amount or % off). */
export function formatSlotTimeOfferChipLabel(offer) {
  if (!offer || offer.offerBasePrice == null) return "";
  const kind = String(offer.offerPriceKind || "RS").toUpperCase().trim();
  const v = Number(offer.offerBasePrice);
  if (!Number.isFinite(v)) return "";
  if (kind === "PCT") {
    const pct = Math.min(100, Math.max(0, Math.round(v)));
    return `${pct}% off`;
  }
  return `₹${Math.round(v)} off`;
}

function stripSlotOfferFields(slot) {
  const {
    slotOfferHeadline,
    regularSlotBasePrice,
    slotTimeOfferId,
    slotOfferActive,
    slotOfferPriceKind,
    slotOfferRawValue,
    slotOfferDisplayLabel,
    ...rest
  } = slot;
  return rest;
}

/** Per-slot catalog promo (Flat ₹ off or % off), when no date-bound slot offer applies. */
export function applySlotListedPromoPricing(slot) {
  if (!slot || typeof slot !== "object" || slot.slotOfferActive) {
    return slot;
  }
  const {
    slotListedRegularBase: _r,
    slotListedPromoActive: _a,
    ...base
  } = slot;
  const kind = String(base.slotPromoKind || "").toUpperCase().trim();
  const listed = Number(base.basePrice ?? 0);
  const v = Number(base.slotPromoValue);
  if (!kind || kind === "NONE" || !Number.isFinite(v) || v <= 0) {
    return { ...base, slotListedPromoActive: false };
  }
  let next = listed;
  if (kind === "FLAT") next = Math.max(0, listed - v);
  else if (kind === "PERCENT") {
    const pct = Math.min(100, Math.max(0, v));
    next = Math.round((listed * (100 - pct)) / 100);
  } else {
    return { ...base, slotListedPromoActive: false };
  }
  return {
    ...base,
    basePrice: next,
    slotListedRegularBase: listed,
    slotListedPromoActive: true,
  };
}

/**
 * Apply date-range slot offer (matches server: uses raw slots_meta base, then slot listed promo only if no date offer).
 */
export function mergeSlotWithOffer(slot, theatreId, eventYmd, slotOffers) {
  if (!slot || typeof slot !== "object" || slot.id == null) return slot;
  const stripped = stripSlotOfferFields(slot);
  const o = findApplicableSlotOffer(slotOffers, theatreId, stripped.id, eventYmd);
  if (!o || o.offerBasePrice == null) {
    return applySlotListedPromoPricing({ ...stripped, slotOfferActive: false });
  }
  const catalogBase = Number(stripped.basePrice ?? 0);
  const kind = String(o.offerPriceKind || "RS").toUpperCase().trim();
  const raw = Number(o.offerBasePrice);
  let finalBase;
  if (kind === "PCT") {
    const pct = Math.min(100, Math.max(0, raw));
    finalBase = Math.round((catalogBase * (100 - pct)) / 100);
  } else {
    /** RS = flat ₹ discount off listed slot base (same as server EventsService). */
    finalBase = Math.max(0, Math.round(catalogBase - raw));
  }
  const displayLabel = formatSlotTimeOfferChipLabel(o);
  return {
    ...stripped,
    basePrice: finalBase,
    slotOfferHeadline:
      o.headline || "Special package rate applies on your selected date for this slot.",
    regularSlotBasePrice: catalogBase,
    slotTimeOfferId: o.id,
    slotOfferActive: true,
    slotOfferPriceKind: kind === "PCT" ? "PCT" : "RS",
    slotOfferRawValue: raw,
    slotOfferDisplayLabel: displayLabel,
    slotListedPromoActive: false,
  };
}
